// Analytics utilities for Google Analytics 4

// GA4 Measurement ID (should match the one in consentAwareAnalytics.js)
const GA4_MEASUREMENT_ID = "G-EJKVDG3H9W";

/**
 * Track page views
 * Uses GA4's recommended method for tracking page views in SPAs
 */
export const trackPageView = (url, title) => {
  if (typeof window !== "undefined" && window.gtag) {
    // Use config method for page views (recommended for SPAs)
    window.gtag("config", GA4_MEASUREMENT_ID, {
      page_path: url,
      page_title: title,
      page_location: window.location.href,
    });

    // Log in development for easier testing
    if (process.env.NODE_ENV === "development") {
      console.log("[GA4] Page view tracked:", { url, title });
    }
  } else if (process.env.NODE_ENV === "development") {
    console.warn("[GA4] gtag not available - tracking skipped");
  }
};

/**
 * Track custom events
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", eventName, eventParams);
  }
};

/**
 * Track booking flow events
 */
export const trackBookingEvent = (step, params = {}) => {
  trackEvent("booking_flow", {
    step,
    ...params,
  });
};

/**
 * Track search queries
 */
export const trackSearch = (searchTerm, category = "general") => {
  trackEvent("search", {
    search_term: searchTerm,
    category,
  });
};

/**
 * Track therapist views
 */
export const trackTherapistView = (therapistId, therapistName) => {
  trackEvent("view_therapist", {
    therapist_id: therapistId,
    therapist_name: therapistName,
  });
};

/**
 * Track booking conversions
 */
export const trackBookingConversion = (bookingId, value, currency = "CAD") => {
  trackEvent("purchase", {
    transaction_id: bookingId,
    value: value,
    currency: currency,
    items: [
      {
        item_name: "Massage Therapy Session",
        item_category: "Services",
        price: value,
        quantity: 1,
      },
    ],
  });
};

/**
 * Track gift card purchases
 */
export const trackGiftCardPurchase = (amount, giftCardId) => {
  trackEvent("purchase", {
    transaction_id: giftCardId,
    value: amount,
    currency: "CAD",
    items: [
      {
        item_name: "Gift Card",
        item_category: "Gift Cards",
        price: amount,
        quantity: 1,
      },
    ],
  });
};

/**
 * Track outbound links
 */
export const trackOutboundLink = (url, label) => {
  trackEvent("outbound_link", {
    link_url: url,
    link_label: label,
  });
};

/**
 * Track user signups
 */
export const trackSignup = (method = "email") => {
  trackEvent("sign_up", {
    method,
  });
};

/**
 * Track user logins
 */
export const trackLogin = (method = "email") => {
  trackEvent("login", {
    method,
  });
};

/**
 * Track loyalty program actions
 */
export const trackLoyaltyAction = (action, points = 0) => {
  trackEvent("loyalty_action", {
    action,
    points_earned: points,
  });
};

/**
 * Track blog article reads
 */
export const trackArticleRead = (articleTitle, articleSlug) => {
  trackEvent("article_view", {
    article_title: articleTitle,
    article_slug: articleSlug,
  });
};

/**
 * Check GA4 consent status from dataLayer
 */
export const checkGAConsent = () => {
  if (typeof window === "undefined" || !window.dataLayer) {
    return { error: "dataLayer not available" };
  }

  // Find the latest consent update in dataLayer
  const consentUpdates = window.dataLayer.filter(
    (item) =>
      Array.isArray(item) && item[0] === "consent" && item[1] === "update",
  );

  if (consentUpdates.length > 0) {
    const latestConsent = consentUpdates[consentUpdates.length - 1][2];
    return {
      analytics_storage: latestConsent.analytics_storage || "unknown",
      ad_storage: latestConsent.ad_storage || "unknown",
      ad_user_data: latestConsent.ad_user_data || "unknown",
      ad_personalization: latestConsent.ad_personalization || "unknown",
    };
  }

  // Check for default consent
  const defaultConsent = window.dataLayer.find(
    (item) =>
      Array.isArray(item) && item[0] === "consent" && item[1] === "default",
  );

  if (defaultConsent) {
    return {
      analytics_storage: defaultConsent[2]?.analytics_storage || "denied",
      ad_storage: defaultConsent[2]?.ad_storage || "denied",
      ad_user_data: defaultConsent[2]?.ad_user_data || "denied",
      ad_personalization: defaultConsent[2]?.ad_personalization || "denied",
      note: "Using default consent (no update found)",
    };
  }

  return { error: "No consent found in dataLayer" };
};

/**
 * Test helper - Check if GA4 is properly initialized
 * Call this in browser console: window.testGA4()
 */
export const testGA4 = () => {
  if (typeof window === "undefined") {
    console.log("❌ Not in browser environment");
    return;
  }

  const checks = {
    gtagAvailable: typeof window.gtag === "function",
    dataLayerExists: Array.isArray(window.dataLayer),
    measurementId: GA4_MEASUREMENT_ID,
    consentStatus: checkGAConsent(),
  };

  console.log("🔍 GA4 Status Check:", checks);

  if (checks.gtagAvailable) {
    console.log("✅ GA4 is initialized and ready!");
    console.log(
      `📊 Test page view with: window.gtag('config', '${GA4_MEASUREMENT_ID}', { page_path: '/test' })`,
    );

    // Show consent status
    if (checks.consentStatus.analytics_storage === "granted") {
      console.log("✅ Analytics consent: GRANTED - Data collection is active!");
    } else if (checks.consentStatus.analytics_storage === "denied") {
      console.log(
        "⚠️ Analytics consent: DENIED - Accept cookies to enable tracking",
      );
    } else {
      console.log("⚠️ Analytics consent: UNKNOWN - Check consent banner");
    }
  } else {
    console.log(
      "❌ GA4 not initialized. Check cookie consent and initialization.",
    );
  }

  return checks;
};

// ============================================
// COMPREHENSIVE TRACKING FUNCTIONS
// ============================================

/**
 * Track form interactions
 */
export const trackFormStart = (formName, formId = null) => {
  trackEvent("form_start", {
    form_name: formName,
    form_id: formId,
    page_path: window.location.pathname,
  });
};

export const trackFormFieldFocus = (
  formName,
  fieldName,
  fieldType = "text",
) => {
  trackEvent("form_field_focus", {
    form_name: formName,
    field_name: fieldName,
    field_type: fieldType,
    page_path: window.location.pathname,
  });
};

export const trackFormFieldBlur = (formName, fieldName, hasValue = false) => {
  trackEvent("form_field_blur", {
    form_name: formName,
    field_name: fieldName,
    has_value: hasValue,
    page_path: window.location.pathname,
  });
};

export const trackFormFieldChange = (
  formName,
  fieldName,
  fieldType = "text",
  valueLength = 0,
) => {
  trackEvent("form_field_change", {
    form_name: formName,
    field_name: fieldName,
    field_type: fieldType,
    value_length: valueLength,
    page_path: window.location.pathname,
  });
};

export const trackFormSubmit = (formName, formId = null, success = true) => {
  trackEvent("form_submit", {
    form_name: formName,
    form_id: formId,
    success: success,
    page_path: window.location.pathname,
  });
};

export const trackFormError = (formName, fieldName, errorType) => {
  trackEvent("form_error", {
    form_name: formName,
    field_name: fieldName,
    error_type: errorType,
    page_path: window.location.pathname,
  });
};

export const trackFormAbandon = (formName, fieldsCompleted = 0) => {
  trackEvent("form_abandon", {
    form_name: formName,
    fields_completed: fieldsCompleted,
    page_path: window.location.pathname,
  });
};

/**
 * Track input field interactions
 */
export const trackInputFocus = (
  inputName,
  inputType = "text",
  inputId = null,
) => {
  trackEvent("input_focus", {
    input_name: inputName,
    input_type: inputType,
    input_id: inputId,
    page_path: window.location.pathname,
  });
};

export const trackInputBlur = (inputName, hasValue = false) => {
  trackEvent("input_blur", {
    input_name: inputName,
    has_value: hasValue,
    page_path: window.location.pathname,
  });
};

export const trackInputChange = (
  inputName,
  inputType = "text",
  valueLength = 0,
) => {
  trackEvent("input_change", {
    input_name: inputName,
    input_type: inputType,
    value_length: valueLength,
    page_path: window.location.pathname,
  });
};

/**
 * Track section visibility
 */
export const trackSectionView = (
  sectionName,
  sectionId = null,
  timeOnPage = 0,
) => {
  trackEvent("section_view", {
    section_name: sectionName,
    section_id: sectionId,
    time_on_page: timeOnPage,
    page_path: window.location.pathname,
  });
};

export const trackSectionScroll = (sectionName, scrollPercentage) => {
  trackEvent("section_scroll", {
    section_name: sectionName,
    scroll_percentage: scrollPercentage,
    page_path: window.location.pathname,
  });
};

/**
 * Track button/link clicks
 */
export const trackButtonClick = (
  buttonName,
  buttonId = null,
  buttonLocation = null,
  buttonType = "button",
) => {
  trackEvent("button_click", {
    button_name: buttonName,
    button_id: buttonId,
    button_location: buttonLocation,
    button_type: buttonType,
    page_path: window.location.pathname,
  });
};

export const trackLinkClick = (linkText, linkUrl, linkLocation = null) => {
  trackEvent("link_click", {
    link_text: linkText,
    link_url: linkUrl,
    link_location: linkLocation,
    page_path: window.location.pathname,
  });
};

/**
 * Track scroll depth
 */
export const trackScrollDepth = (depth, depthType = "percentage") => {
  trackEvent("scroll_depth", {
    depth: depth,
    depth_type: depthType, // 'percentage' or 'pixels'
    page_path: window.location.pathname,
  });
};

/**
 * Track time on page/section
 */
export const trackTimeOnPage = (timeInSeconds, pageName = null) => {
  trackEvent("time_on_page", {
    time_seconds: timeInSeconds,
    page_name: pageName || window.location.pathname,
  });
};

export const trackTimeOnSection = (sectionName, timeInSeconds) => {
  trackEvent("time_on_section", {
    section_name: sectionName,
    time_seconds: timeInSeconds,
    page_path: window.location.pathname,
  });
};

/**
 * Track video/media interactions
 */
export const trackVideoPlay = (videoName, videoId = null) => {
  trackEvent("video_play", {
    video_name: videoName,
    video_id: videoId,
    page_path: window.location.pathname,
  });
};

export const trackVideoPause = (videoName, currentTime = 0) => {
  trackEvent("video_pause", {
    video_name: videoName,
    current_time: currentTime,
    page_path: window.location.pathname,
  });
};

export const trackVideoComplete = (videoName, duration = 0) => {
  trackEvent("video_complete", {
    video_name: videoName,
    duration: duration,
    page_path: window.location.pathname,
  });
};

/**
 * Track modal/dialog interactions
 */
export const trackModalOpen = (modalName, modalId = null, trigger = null) => {
  trackEvent("modal_open", {
    modal_name: modalName,
    modal_id: modalId,
    trigger: trigger,
    page_path: window.location.pathname,
  });
};

export const trackModalClose = (modalName, timeOpen = 0) => {
  trackEvent("modal_close", {
    modal_name: modalName,
    time_open: timeOpen,
    page_path: window.location.pathname,
  });
};

/**
 * Track dropdown/select interactions
 */
export const trackDropdownOpen = (dropdownName, dropdownId = null) => {
  trackEvent("dropdown_open", {
    dropdown_name: dropdownName,
    dropdown_id: dropdownId,
    page_path: window.location.pathname,
  });
};

export const trackDropdownSelect = (dropdownName, selectedValue) => {
  trackEvent("dropdown_select", {
    dropdown_name: dropdownName,
    selected_value: selectedValue,
    page_path: window.location.pathname,
  });
};

/**
 * Track accordion/tab interactions
 */
export const trackAccordionOpen = (accordionName, accordionItem) => {
  trackEvent("accordion_open", {
    accordion_name: accordionName,
    accordion_item: accordionItem,
    page_path: window.location.pathname,
  });
};

export const trackTabSwitch = (tabGroup, tabName) => {
  trackEvent("tab_switch", {
    tab_group: tabGroup,
    tab_name: tabName,
    page_path: window.location.pathname,
  });
};

/**
 * Track file uploads
 */
export const trackFileUpload = (fileName, fileType, fileSize = 0) => {
  trackEvent("file_upload", {
    file_name: fileName,
    file_type: fileType,
    file_size: fileSize,
    page_path: window.location.pathname,
  });
};

/**
 * Track copy actions
 */
export const trackCopyAction = (copiedText, copyLocation = null) => {
  trackEvent("copy_action", {
    copied_text: copiedText.substring(0, 50), // Limit length
    copy_location: copyLocation,
    page_path: window.location.pathname,
  });
};

/**
 * Track share actions
 */
export const trackShare = (shareMethod, sharedContent, sharedUrl = null) => {
  trackEvent("share", {
    share_method: shareMethod,
    shared_content: sharedContent,
    shared_url: sharedUrl,
    page_path: window.location.pathname,
  });
};

/**
 * Track errors
 */
export const trackError = (errorType, errorMessage, errorLocation = null) => {
  trackEvent("error", {
    error_type: errorType,
    error_message: errorMessage.substring(0, 100), // Limit length
    error_location: errorLocation || window.location.pathname,
  });
};

/**
 * Track engagement (hover, mouse movement, etc.)
 */
export const trackEngagement = (engagementType, elementName, duration = 0) => {
  trackEvent("engagement", {
    engagement_type: engagementType, // 'hover', 'mouse_move', 'key_press', etc.
    element_name: elementName,
    duration: duration,
    page_path: window.location.pathname,
  });
};

// Make test functions available globally in development
if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
  window.testGA4 = testGA4;
  window.checkGAConsent = checkGAConsent;
}
